// src/screens/AllocationScreen.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Brief 37 Section 1 — Your Allocation (redesigned)
// Starts from gross income, shows tax deduction, net take-home, then allocation
// categories. All percentages are % of gross. Company pension in separate callout.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useMemo } from 'react';
import { ClientData } from '../types/ClientData';
import { getTaxYearForDate, TAX_YEARS } from '../taxConfig';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── calcIncome — derive gross, tax, and net monthly income ──────────────────
function calcIncome(data: ClientData): { grossMonthly: number; totalMonthlyTax: number; netMonthly: number } {
  const taxYear = data.taxYearOverride ?? getTaxYearForDate(data.sessionDate || new Date().toISOString());
  const r = TAX_YEARS[taxYear];

  const salary     = data.salary || 0;
  const payeIncome = data.payeIncome || 0;
  const rentalNet  = data.rentalIncomeNet || 0;
  const savingsInt = data.savingsInterest || 0;
  const companyDiv = data.dividends || 0;
  const otherDiv   = data.otherDividends || 0;
  const expenses   = (data.expensesReimbursement || 0) * 12;

  const nonSavingsIncome = salary + payeIncome + rentalNet;
  const savingsIncome    = savingsInt;
  const totalDividends   = companyDiv + otherDiv;
  const totalTaxableIncome = nonSavingsIncome + savingsIncome + totalDividends;

  let effectivePA: number = r.personalAllowance;
  if (totalTaxableIncome > 100000) {
    const taper = Math.floor((totalTaxableIncome - 100000) / 2);
    effectivePA = Math.max(0, r.personalAllowance - taper);
  }

  const bandPA = effectivePA;
  const bandBasicTop = r.basicRateLimit;
  const bandHigherTop = r.higherRateLimit;

  function fillBands(amount: number, cursor: number) {
    let remaining = amount;
    let inPA = 0, inBasic = 0, inHigher = 0, inAdditional = 0;
    if (cursor < bandPA && remaining > 0) { const fill = Math.min(remaining, bandPA - cursor); inPA = fill; cursor += fill; remaining -= fill; }
    if (cursor < bandBasicTop && remaining > 0) { const fill = Math.min(remaining, bandBasicTop - cursor); inBasic = fill; cursor += fill; remaining -= fill; }
    if (cursor < bandHigherTop && remaining > 0) { const fill = Math.min(remaining, bandHigherTop - cursor); inHigher = fill; cursor += fill; remaining -= fill; }
    if (remaining > 0) { inAdditional = remaining; cursor += remaining; }
    return { inPA, inBasic, inHigher, inAdditional, newCursor: cursor };
  }

  const ns = fillBands(nonSavingsIncome, 0);
  const sv = fillBands(savingsIncome, ns.newCursor);
  const dv = fillBands(totalDividends, sv.newCursor);

  const itNonSavings = ns.inBasic * r.incomeTaxBasic + ns.inHigher * r.incomeTaxHigher + ns.inAdditional * r.incomeTaxAdditional;

  const highestNonSavingsBand = ns.inAdditional > 0 ? 'additional' : ns.inHigher > 0 ? 'higher' : 'basic';
  const psa = highestNonSavingsBand === 'additional' ? 0 : highestNonSavingsBand === 'higher' ? 500 : 1000;
  const taxableSavings = Math.max(0, savingsIncome - psa);
  const svTaxableBasic = Math.max(0, Math.min(sv.inBasic, taxableSavings));
  const svTaxableHigher = Math.max(0, Math.min(sv.inHigher, taxableSavings - svTaxableBasic));
  const svTaxableAdditional = Math.max(0, taxableSavings - svTaxableBasic - svTaxableHigher);
  const itSavings = svTaxableBasic * r.incomeTaxBasic + svTaxableHigher * r.incomeTaxHigher + svTaxableAdditional * r.incomeTaxAdditional;

  const taxableDivs = Math.max(0, totalDividends - r.dividendAllowance);
  const dvTaxableBasic = Math.min(dv.inBasic, taxableDivs);
  const dvTaxableHigher = Math.min(dv.inHigher, Math.max(0, taxableDivs - dvTaxableBasic));
  const dvTaxableAdditional = Math.max(0, taxableDivs - dvTaxableBasic - dvTaxableHigher);
  const dividendTax = dvTaxableBasic * r.dividendBasicRate + dvTaxableHigher * r.dividendHigherRate + dvTaxableAdditional * r.dividendAdditionalRate;

  const niableSalary = Math.max(0, (data.salary || 0) - r.niPrimaryThreshold);
  const niBasicAmount = Math.min(niableSalary, Math.max(0, r.niUpperEarningsLimit - r.niPrimaryThreshold));
  const niUpperAmount = Math.max(0, niableSalary - niBasicAmount);
  const niEmployee = niBasicAmount * r.niEmployeeRate + niUpperAmount * r.niEmployeeUpperRate;

  let slUndergradAnnual = 0;
  const slPlan = data.studentLoanUndergrad || 'none';
  if (slPlan !== 'none') {
    const threshold = slPlan === 'plan1' ? r.slPlan1Threshold : slPlan === 'plan2' ? r.slPlan2Threshold : r.slPlan5Threshold;
    slUndergradAnnual = Math.max(0, totalTaxableIncome - threshold) * r.slUndergradRate;
  }
  const slPostgradAnnual = data.studentLoanPostgrad ? Math.max(0, totalTaxableIncome - r.slPostgradThreshold) * r.slPostgradRate : 0;
  const slTotalAnnual = slUndergradAnnual + slPostgradAnnual;

  const totalAnnualTax = itNonSavings + itSavings + dividendTax + niEmployee + slTotalAnnual;
  const totalMonthlyTax = totalAnnualTax / 12;
  const grossMonthly = (totalTaxableIncome / 12) + (expenses / 12);
  const netMonthly = grossMonthly - totalMonthlyTax;

  return { grossMonthly, totalMonthlyTax, netMonthly };
}

export default function AllocationScreen({ data, onUpdate }: Props) {
  const [showAssetDetail, setShowAssetDetail] = useState(true);
  const [mortgageExpanded, setMortgageExpanded] = useState(true);
  const [essentialsExpanded, setEssentialsExpanded] = useState(true);
  const [lifestyleExpanded, setLifestyleExpanded] = useState(true);
  const [allExpanded, setAllExpanded] = useState(true);

  const toggleAll = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setShowAssetDetail(next);
    setMortgageExpanded(next);
    setEssentialsExpanded(next);
    setLifestyleExpanded(next);
  };

  const { grossMonthly, totalMonthlyTax, netMonthly } = useMemo(() => calcIncome(data), [data]);

  // ── Mortgage capital/interest split ─────────────────────────────────────────
  // mortgageRate stored as percentage (e.g. 4.5), convert to decimal
  const monthlyRate = ((data.mortgageRate || 0) / 100) / 12;
  const monthlyInterest = (data.mortgageBalance || 0) * monthlyRate;
  const monthlyCapital = Math.max(0, (data.mortgagePayment || 0) - monthlyInterest);

  // ── Personal savings — employee contributions only (no mortgage capital) ──
  const employeePension = data.pensionMonthly || 0;
  const isaContrib = data.isaContribution || 0;
  const savingsTopUp = data.efTopUp || 0;
  const personalAssets = employeePension + isaContrib + savingsTopUp;

  // Company pension — separate callout, NOT included in personal allocation
  const pensionCompanyFunded = data.pensionCompanyFunded || 0;

  // Essentials
  const essentials = (data.essentialsUtilities || 0) + (data.essentialsFood || 0) + (data.essentialsTravel || 0);

  // Lifestyle remainder — corrected (Brief 38)
  const lifestyle = Math.max(0,
    netMonthly
    - (data.mortgagePayment || 0)   // full mortgage payment (capital + interest)
    - essentials
    - employeePension               // employee pension from take-home
    - isaContrib                    // ISA contribution
    - savingsTopUp                  // savings account top-up
  );

  // Update derived lifestyle monthly
  React.useEffect(() => {
    if (Math.round(lifestyle) !== data.lifestyleMonthly) {
      onUpdate({ lifestyleMonthly: Math.round(lifestyle) });
    }
  }, [lifestyle, data.lifestyleMonthly, onUpdate]);

  // ── Percentages — all as % of gross ───────────────────────────────────────
  const gross = grossMonthly || 1;
  const taxPct = (totalMonthlyTax / gross) * 100;
  const netPct = (netMonthly / gross) * 100;
  const mortgagePct = ((data.mortgagePayment || 0) / gross) * 100;
  const essentialsPct = (essentials / gross) * 100;
  const personalAssetsPct = (personalAssets / gross) * 100;
  const lifestylePct = (lifestyle / gross) * 100;

  // Effective tax rate
  const effectiveRate = grossMonthly > 0 ? (totalMonthlyTax / grossMonthly * 100) : 0;

  // ── Bar chart — different grouping (Tax | Assets | Debt | Essentials | Lifestyle) ─
  const barAssets = monthlyCapital          // mortgage capital repayment
    + employeePension                       // employee pension
    + isaContrib                            // ISA contribution
    + savingsTopUp                          // savings account top-up
    + (data.pensionCompanyFunded || 0);     // company pension
  const barDebt = monthlyInterest;          // mortgage interest only
  const barLifestyle = Math.max(0, grossMonthly - totalMonthlyTax - barAssets - barDebt - essentials);

  const barTaxPct = (totalMonthlyTax / gross) * 100;
  const barAssetsPct = (barAssets / gross) * 100;
  const barDebtPct = (barDebt / gross) * 100;
  const barEssentialsPct = (essentials / gross) * 100;
  const barLifestylePct = Math.max(0, (barLifestyle / gross) * 100);

  // Colours
  const GOLD = '#C9A84C';
  const MORTGAGE_DOT = '#B8860B';   // mortgage row dot colour
  const DEBT_BAR = '#EF4444';       // bar chart debt segment (mortgage red from potColours)
  const RED_ORANGE = '#E5603B';
  const NAVY = '#1B2B4B';
  const SLATE = '#94A3B8';

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-6 max-w-5xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-ma-navy">Your Allocation</h2>
          <p className="text-gray-500 mt-1 text-sm">Every pound accounted for — from gross income down.</p>
        </div>

        {/* Gross → Tax → Net header section */}
        <div className="bg-gray-50 rounded-t-2xl border border-gray-200 border-b-0 px-6 py-4 mb-0">
          <div className="flex items-center justify-between py-2">
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Gross Income</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(grossMonthly))}/mo</span>
              <span className="text-xs text-gray-400 ml-2">100%</span>
            </div>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <span className="text-xs text-gray-500">Tax & NI ({effectiveRate.toFixed(1)}% effective rate)</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-medium text-red-600">−{fmt(Math.round(totalMonthlyTax))}/mo</span>
              <span className="text-xs text-gray-400 ml-2">{taxPct.toFixed(0)}%</span>
            </div>
          </div>
          <div className="border-t border-gray-300 mt-1 pt-2 flex items-center justify-between py-2">
            <div>
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Net Take-Home</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(netMonthly))}/mo</span>
              <span className="text-xs text-gray-400 ml-2">{netPct.toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Allocation Breakdown */}
        <div className="bg-white rounded-b-2xl border border-gray-200 border-t-0 p-6 mb-6">
          <div className="flex justify-end mb-2">
            <button
              onClick={toggleAll}
              className="text-xs text-gray-500 hover:text-ma-navy transition-colors font-medium"
            >
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
          <div className="space-y-0">
            {/* Mortgage */}
            {(data.mortgagePayment || 0) > 0 && (
              <>
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer hover:bg-amber-50 transition-colors"
                  onClick={() => setMortgageExpanded(!mortgageExpanded)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: MORTGAGE_DOT }} />
                    <span className="text-sm font-semibold text-ma-navy">Mortgage</span>
                    <span className="text-xs text-gray-400">{mortgageExpanded ? '▾' : '▸'}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(data.mortgagePayment || 0))}/mo</span>
                    <span className="text-xs text-gray-400 ml-2">{mortgagePct.toFixed(0)}%</span>
                  </div>
                </div>
                {mortgageExpanded && (
                  <div className="ml-10 mb-2 space-y-1">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>→ Capital repayment</span>
                      <span className="text-gray-700">{fmt(Math.round(monthlyCapital))}/mo</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>→ Interest (cost)</span>
                      <span className="text-gray-700">{fmt(Math.round(monthlyInterest))}/mo</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Balance</span>
                      <span className="text-gray-700">{fmt(data.mortgageBalance || 0)}</span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Rate</span>
                      <span className="text-gray-700">{((data.mortgageRate || 0)).toFixed(2)}%</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Essential Needs */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => setEssentialsExpanded(!essentialsExpanded)}
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: NAVY }} />
                <span className="text-sm font-semibold text-ma-navy">Essentials</span>
                <span className="text-xs text-gray-400">{essentialsExpanded ? '▾' : '▸'}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(essentials))}/mo</span>
                <span className="text-xs text-gray-400 ml-2">{essentialsPct.toFixed(0)}%</span>
              </div>
            </div>
            {essentialsExpanded && (
              <div className="ml-10 mb-2 space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Monthly essentials</span>
                  <span className="text-gray-700">{fmt(Math.round(essentials))}/mo</span>
                </div>
                <div className="text-[10px] text-gray-400 mt-1">Bills, groceries and regular outgoings outside of mortgage</div>
              </div>
            )}

            {/* Savings */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer hover:bg-amber-50 transition-colors"
              onClick={() => setShowAssetDetail(!showAssetDetail)}
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: GOLD }} />
                <span className="text-sm font-semibold text-ma-navy">Savings</span>
                <span className="text-xs text-gray-400">{showAssetDetail ? '▾' : '▸'}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(personalAssets))}/mo</span>
                <span className="text-xs text-gray-400 ml-2">{personalAssetsPct.toFixed(0)}%</span>
              </div>
            </div>
            {showAssetDetail && (
              <div className="ml-10 mb-2 space-y-1">
                {employeePension > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>→ Employee pension</span>
                    <span className="text-gray-700">{fmt(employeePension)}/mo</span>
                  </div>
                )}
                {isaContrib > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>→ ISA contributions</span>
                    <span className="text-gray-700">{fmt(isaContrib)}/mo</span>
                  </div>
                )}
                {savingsTopUp > 0 && (
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>→ Savings account contribution</span>
                    <span className="text-gray-700">{fmt(savingsTopUp)}/mo</span>
                  </div>
                )}
              </div>
            )}

            {/* Lifestyle */}
            <div
              className="flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setLifestyleExpanded(!lifestyleExpanded)}
            >
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: SLATE }} />
                <span className="text-sm font-semibold text-ma-navy">Remainder (lifestyle)</span>
                <span className="text-xs text-gray-400">{lifestyleExpanded ? '▾' : '▸'}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(lifestyle))}/mo</span>
                <span className="text-xs text-gray-400 ml-2">{lifestylePct.toFixed(0)}%</span>
              </div>
            </div>
            {lifestyleExpanded && (
              <div className="ml-10 mb-2 space-y-1">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Derived figure</span>
                  <span className="text-gray-700">{fmt(Math.round(lifestyle))}/mo</span>
                </div>
                <div className="text-[10px] text-gray-400 mt-1">Take-home minus all committed allocations. Not entered directly.</div>
              </div>
            )}

            {/* Take-home total */}
            <div className="border-t border-gray-200 mt-2 pt-3 flex items-center justify-between px-4 py-2">
              <span className="text-sm font-bold text-ma-navy">Take-home total</span>
              <div className="text-right">
                <span className="text-sm font-bold text-ma-navy">{fmt(Math.round(netMonthly))}/mo</span>
                <span className="text-xs text-gray-400 ml-2">100% of net</span>
              </div>
            </div>
          </div>
        </div>

        {/* Section 1B — Employer Pension Callout Card */}
        {pensionCompanyFunded > 0 && (
          <div className="border-l-4 border-ma-navy bg-blue-50 rounded-r-2xl p-5 mb-6">
            <div className="text-sm font-bold text-ma-navy mb-2">Employer Pension Contribution</div>
            <p className="text-sm text-gray-700 mb-3">
              {fmt(pensionCompanyFunded)}/mo paid directly by {data.businessName || 'your company'} — not from your take-home.
            </p>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Combined savings & pension</span>
                <span className="font-bold text-ma-navy">{fmt(Math.round(personalAssets + pensionCompanyFunded))}/mo</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">As % of gross income</span>
                <span className="font-bold text-ma-navy">{((personalAssets + pensionCompanyFunded) / gross * 100).toFixed(1)}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Stacked Bar Chart — Tax | Assets | Debt | Essentials | Lifestyle */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <div className="flex h-10 rounded-full overflow-hidden">
            {barTaxPct > 0 && (
              <div
                className="flex items-center justify-center text-white text-xs font-bold"
                style={{ width: `${barTaxPct}%`, backgroundColor: RED_ORANGE }}
              >
                {barTaxPct >= 5 ? `${barTaxPct.toFixed(0)}%` : ''}
              </div>
            )}
            {barAssetsPct > 0 && (
              <div
                className="flex items-center justify-center text-white text-xs font-bold"
                style={{ width: `${barAssetsPct}%`, backgroundColor: GOLD }}
              >
                {barAssetsPct >= 5 ? `${barAssetsPct.toFixed(0)}%` : ''}
              </div>
            )}
            {barDebtPct > 0 && (
              <div
                className="flex items-center justify-center text-white text-xs font-bold"
                style={{ width: `${barDebtPct}%`, backgroundColor: DEBT_BAR }}
              >
                {barDebtPct >= 5 ? `${barDebtPct.toFixed(0)}%` : ''}
              </div>
            )}
            {barEssentialsPct > 0 && (
              <div
                className="flex items-center justify-center text-white text-xs font-bold"
                style={{ width: `${barEssentialsPct}%`, backgroundColor: NAVY }}
              >
                {barEssentialsPct >= 5 ? `${barEssentialsPct.toFixed(0)}%` : ''}
              </div>
            )}
            {barLifestylePct > 0 && (
              <div
                className="flex items-center justify-center text-white text-xs font-bold"
                style={{ width: `${barLifestylePct}%`, backgroundColor: SLATE }}
              >
                {barLifestylePct >= 5 ? `${barLifestylePct.toFixed(0)}%` : ''}
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="flex flex-wrap gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: RED_ORANGE }} />Tax</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: GOLD }} />Assets</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: DEBT_BAR }} />Debt</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: NAVY }} />Essentials</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: SLATE }} />Lifestyle</span>
          </div>
        </div>

        {/* Asset building rate */}
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-5 mb-6">
          <p className="text-sm text-gray-700 leading-relaxed">
            Your asset building rate: <span className="font-bold text-ma-navy">{barAssetsPct.toFixed(0)}%</span> of gross — includes your company pension and mortgage capital repayment.
            Generic rules say 15–25%. FFM doesn't use a generic guide. Today we build your actual number — the specific monthly target that reaches your Freedom Number. Your company pension means a significant portion of this never touches your take-home pay.
          </p>
        </div>

        {/* Source */}
        <div className="text-center text-[10px] text-gray-400 mb-6">
          Allocation derived from your income and commitments. Mortgage split based on {(data.mortgageRate || 0).toFixed(1)}% annual rate. All percentages shown as % of gross income.
        </div>
      </div>
    </div>
  );
}
